import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "IncomePredictorReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    income_predictor_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        income_prediction: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some((option) => option);
    },
    safe_income_predictor_data() {
      let data = this.income_predictor_data || {};
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse income_predictor_data as JSON:", e);
          data = {};
        }
      }
      return data;
    },

    // Income predictor data helpers
    status() {
      return this.safe_income_predictor_data.status || "N/A";
    },

    result() {
      return this.safe_income_predictor_data;
    },

    results() {
      return this.result.RESULTS || {};
    },

    transaction_completed() {
      return this.results.TRANSACTION_COMPLETED || "N/A";
    },

    ret_data() {
      return this.results.RET_DATA || {};
    },

    root() {
      return this.ret_data.ROOT || {};
    },

    id_number() {
      return this.root.ID_NUMBER || "N/A";
    },

    gmip_value() {
      return this.root.GMIPValue || "N/A";
    },

    gmip_confidence_level() {
      return this.root.GMIPConfidenceLevel || "N/A";
    },

    gmip_band() {
      return this.root.GMIPBand || "N/A";
    },

    // Report metadata
    report_reference() {
      return "INP-" + Date.now();
    },

    generation_date() {
      return new Date().toISOString();
    },

    formatted_generation_date() {
      return this.formatDate(this.generation_date);
    },

    modal_instance() {
      var modal = new window.bootstrap.Modal(
        document.getElementById(`export-modal-${this.report_reference}`)
      );
      return modal;
    },
  },

  methods: {
    close_modal() {
      this.modal_instance.hide();
    },

    // Utility methods
    formatDate(date) {
      return format_date(date);
    },

    // Export functionality
    show_export_modal() {
      this.export_options.income_prediction = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },

    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script(
          "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"
        );
        this.libraries_loaded.papa_parse = true;
      }

      if (!this.libraries_loaded.sheetjs) {
        await this.load_script(
          "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
        );
        this.libraries_loaded.sheetjs = true;
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async export_data() {
      await this.load_export_libraries();

      if (this.export_format === "csv") {
        await this.export_csv();
      } else if (this.export_format === "excel") {
        await this.export_excel();
      } else if (this.export_format === "json") {
        await this.export_json();
      }

      // Close modal
      this.close_modal();
    },

    // Prepare income prediction data
    prepare_income_prediction_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "ID Number": this.id_number,
          "GMIP Value": this.gmip_value,
          "Confidence Level": this.gmip_confidence_level,
          "GMIP Band": this.gmip_band,
          "Transaction Completed": this.transaction_completed,
          "Generated Date": this.formatted_generation_date,
        },
      ];
    },

    // Prepare raw JSON data
    prepare_raw_json_data() {
      return [this.safe_income_predictor_data];
    },

    // Validation method to test export data completeness
    validate_export_data() {
      // Implement validation logic if needed
      return true;
    },

    // Export methods
    async export_csv() {
      let data = [];

      if (this.export_options.income_prediction) {
        data = data.concat(this.prepare_income_prediction_data());
      }

      if (this.export_options.raw_json) {
        data = data.concat(this.prepare_raw_json_data());
      }

      const csv = window.Papa.unparse(data);
      this.download_file(
        csv,
        `income_predictor_report_${this.report_reference}.csv`,
        "text/csv"
      );
    },

    async export_excel() {
      const wb = window.XLSX.utils.book_new();

      if (this.export_options.income_prediction) {
        const ws = window.XLSX.utils.json_to_sheet(
          this.prepare_income_prediction_data()
        );
        window.XLSX.utils.book_append_sheet(wb, ws, "Income Prediction");
      }

      if (this.export_options.raw_json) {
        const ws = window.XLSX.utils.json_to_sheet(
          this.prepare_raw_json_data()
        );
        window.XLSX.utils.book_append_sheet(wb, ws, "Raw JSON");
      }

      window.XLSX.writeFile(
        wb,
        `income_predictor_report_${this.report_reference}.xlsx`
      );
    },

    async export_json() {
      let data = {};

      if (this.export_options.income_prediction) {
        data.income_prediction = this.prepare_income_prediction_data();
      }

      if (this.export_options.raw_json) {
        data.raw_json = this.prepare_raw_json_data();
      }

      const json = JSON.stringify(data, null, 2);
      this.download_file(
        json,
        `income_predictor_report_${this.report_reference}.json`,
        "application/json"
      );
    },

    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
